import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ConfirmationModal from "../../../../../Components/ConfirmationModal/ConfirmationModal"
import { MobergTheme } from "../../../../../Moberg"
import {
	Deployment,
	useEnvironmentVariablesProvider,
} from "../../../../../Providers/EnvironmentVariablesProvider"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { parsePrefetchError } from "../prefetch/errors"
import { usePrefetchSnapshot, useStartPreload } from "../prefetch/hooks"
import { getEntry, getQueueStatusLabel } from "../prefetch/selectors"

const SNAPSHOT_FAILURE_LIMIT = 3
const MAX_PREFETCH_WAIT_MS = 60 * 60 * 1000

type PrefetchPhase = "idle" | "starting" | "watching" | "done" | "skipped"

type PrefetchMachineState = {
	phase: PrefetchPhase
	prefetchMessage: string
	startedAt: number | null
}

// There is no client-side space check at review entry: the picker modal
// gates Start preload on available space, and the server rejects an enqueue
// that will not fit with INSUFFICIENT_SPACE, which surfaces here through the
// failure modal.
interface UsePrefetchDataProps {
	// The first id is the object under review and the only one sent to the
	// server, which expands the preload to its associated sources. Every id is
	// watched for completion.
	dataObjectIds: number[]
	onPrefetchFailureNavigateBack: () => void
}

interface UsePrefetchDataAPI {
	prefetchMessage: string
	prefetchIsFinished: boolean
	// A preload is in flight and progressing; mutually exclusive with
	// prefetchIsFinished (starting/watching vs done/skipped phases).
	prefetchIsActive: boolean
	startPrefetch: () => void
	skipPrefetch: () => void
}

export const usePrefetchData = ({
	dataObjectIds,
	onPrefetchFailureNavigateBack,
}: UsePrefetchDataProps): UsePrefetchDataAPI => {
	const { createModal } = useModalProvider()
	const { deployment } = useEnvironmentVariablesProvider()
	const prefetchDisabled = deployment === Deployment.CLINICAL
	const [prefetchState, setPrefetchState] = useState<PrefetchMachineState>(() => ({
		phase: prefetchDisabled ? "done" : "idle",
		prefetchMessage: "Setting up files...",
		startedAt: null,
	}))
	const { snapshot, isError, isSuccess, error, errorUpdatedAt } =
		usePrefetchSnapshot({
			refetchInterval: prefetchState.phase === "watching" ? 1000 : false,
		})
	const startPreloadMutation = useStartPreload()
	const initialSnapshotErrorShownRef = useRef<boolean>(false)
	const failureModalOpenRef = useRef<boolean>(false)
	const consecutiveSnapshotFailuresRef = useRef<number>(0)
	const lastErrorUpdatedAtRef = useRef<number>(0)
	const preloadTargets = useMemo(
		() => [...new Set(dataObjectIds)].filter(Number.isFinite),
		[dataObjectIds],
	)
	const targetEntries = preloadTargets
		.map(dataObjectId => getEntry(snapshot.files, dataObjectId))
		.filter((entry): entry is NonNullable<typeof entry> => entry !== undefined)
	const completedTargetCount = targetEntries.filter(entry => entry.state === "complete").length
	const allTargetsComplete = preloadTargets.length > 0 && completedTargetCount === preloadTargets.length
	const failedTarget = targetEntries.find(entry => entry.state === "failed" || entry.state === "cancelled")
	const activeTarget = targetEntries.find(entry => entry.state === "running")
		?? targetEntries.find(entry => entry.state === "queued")

	const openPrefetchErrorModal = useCallback((message: string) => {
		createModal(
			<ConfirmationModal
				title="There was a problem"
				description={message}
				confirmButton={{
					text: "OK",
					theme: MobergTheme.BLUE,
				}}
				showCancelButton={false}
			/>
		)
	}, [createModal])

	const openPrefetchFailureModal = useCallback(
		(description: string) => {
			createModal(
				<ConfirmationModal
					title="Preload failed"
					description={description}
					confirmButton={{ text: "Back to patients", theme: MobergTheme.BLUE }}
					showCancelButton={false}
					afterClose={onPrefetchFailureNavigateBack}
				/>
			)
		},
		[createModal, onPrefetchFailureNavigateBack]
	)

	const failPrefetch = useCallback((description: string) => {
		if (failureModalOpenRef.current) {
			return
		}

		failureModalOpenRef.current = true
		setPrefetchState((previous) => ({
			...previous,
			phase: "idle",
			startedAt: null,
		}))
		openPrefetchFailureModal(description)
	}, [openPrefetchFailureModal])

	const startPrefetch = useCallback(() => {
		if (
			prefetchDisabled ||
			prefetchState.phase === "starting" ||
			prefetchState.phase === "watching"
		) {
			return
		}

		failureModalOpenRef.current = false
		consecutiveSnapshotFailuresRef.current = 0
		lastErrorUpdatedAtRef.current = 0
		setPrefetchState({
			phase: "starting",
			prefetchMessage: "Setting up files...",
			startedAt: null,
		})

		void startPreloadMutation.mutateAsync(preloadTargets[0])
			.then(() => {
				setPrefetchState({
					phase: "watching",
					prefetchMessage: "Setting up files...",
					startedAt: Date.now(),
				})
			})
			.catch((reason: unknown) => {
				setPrefetchState({
					phase: "idle",
					prefetchMessage: "Setting up files...",
					startedAt: null,
				})
				failureModalOpenRef.current = true
				openPrefetchFailureModal(parsePrefetchError(reason))
			})
	}, [
		openPrefetchFailureModal,
		prefetchDisabled,
		prefetchState.phase,
		preloadTargets,
		startPreloadMutation,
	])

	useEffect(() => {
		if (prefetchDisabled || prefetchState.phase !== "idle" || !isError) {
			return
		}
		if (initialSnapshotErrorShownRef.current) {
			return
		}

		initialSnapshotErrorShownRef.current = true
		openPrefetchErrorModal(
			`We're not sure where you are in the preloading queue. Please notify your admin that something went wrong. ${parsePrefetchError(error)}`
		)
	}, [
		error,
		isError,
		openPrefetchErrorModal,
		prefetchDisabled,
		prefetchState.phase,
	])

	useEffect(() => {
		if (prefetchState.phase !== "watching") {
			return
		}

		if (isError && errorUpdatedAt > lastErrorUpdatedAtRef.current) {
			lastErrorUpdatedAtRef.current = errorUpdatedAt
			consecutiveSnapshotFailuresRef.current += 1
			if (consecutiveSnapshotFailuresRef.current >= SNAPSHOT_FAILURE_LIMIT) {
				failPrefetch("Preload status could not be refreshed. Please try again.")
				return
			}
		} else if (isSuccess) {
			consecutiveSnapshotFailuresRef.current = 0
			lastErrorUpdatedAtRef.current = 0
		}

		if (allTargetsComplete) {
			setPrefetchState({
				phase: "done",
				prefetchMessage: "Files loaded",
				startedAt: null,
			})
			return
		}

		if (failedTarget) {
			failPrefetch("Preload failed.")
			return
		}

		let message = "Setting up files..."
		const queueStatusLabel = getQueueStatusLabel(activeTarget)
		if (queueStatusLabel) {
			message = `Waiting to preload data... ${queueStatusLabel}`
		} else if (activeTarget?.state === "running") {
			const percent = Number(activeTarget.percent_loaded ?? 0)
			const progress = preloadTargets.length > 1 ? ` (${completedTargetCount}/${preloadTargets.length} files loaded)` : ""
			message = `Preloading data ${Number.isFinite(percent) ? percent.toFixed(1) : "0.0"}%${progress}`
		}

		if (
			targetEntries.length < preloadTargets.length &&
			prefetchState.startedAt !== null &&
			Date.now() - prefetchState.startedAt > MAX_PREFETCH_WAIT_MS
		) {
			failPrefetch("Preload did not complete in the expected amount of time.")
			return
		}

		if (prefetchState.prefetchMessage !== message) {
			setPrefetchState((previous) => ({
				...previous,
				prefetchMessage: message,
			}))
		}
	}, [
		activeTarget,
		allTargetsComplete,
		completedTargetCount,
		errorUpdatedAt,
		failedTarget,
		failPrefetch,
		isError,
		isSuccess,
		prefetchState.phase,
		prefetchState.prefetchMessage,
		prefetchState.startedAt,
		preloadTargets.length,
		targetEntries.length,
	])

	const skipPrefetch = useCallback(() => {
		if (prefetchDisabled) {
			return
		}

		setPrefetchState({
			phase: "skipped",
			prefetchMessage: prefetchState.prefetchMessage,
			startedAt: null,
		})
	}, [prefetchDisabled, prefetchState.prefetchMessage])

	return {
		prefetchMessage: prefetchState.prefetchMessage,
		prefetchIsFinished: prefetchState.phase === "done" || prefetchState.phase === "skipped",
		prefetchIsActive: prefetchState.phase === "starting" || prefetchState.phase === "watching",
		startPrefetch,
		skipPrefetch,
	}
}
